import { useCallback, useMemo } from "react";
import { VictoryChart, VictoryBar, VictoryAxis } from "victory";
import { dollars, modules } from "byzantine";
import Filters from "byzantine/src/filters";

import { ContentCard } from "@narmi/design_system";
import { useLocalization } from "@fluent/react";

import type { DataPoint } from "byzantine/src/dbbl/businessLogic/entities/subscriptions";

import styles from "./SubscriptionsChart.module.scss";

interface SubscriptionsChartProps {
  data: DataPoint[];
  monthLabels: Record<string, string>;
  selectedMonth: string;
  prevMonth: YearMonthString | null;
  nextMonth: YearMonthString | null;
  selectedMonthFullName: string;
  year: string;
  isSelectedThisMonth: boolean;
  prev12MonthsTotal: number;
  monthTotal: number;
  onMonthChange: (month: YearMonthString) => void;
  screenSize: keyof typeof modules.subscriptions.webConfigs;
}

const SubscriptionsChart = ({
  data,
  monthLabels,
  selectedMonth,
  prevMonth,
  nextMonth,
  selectedMonthFullName,
  year,
  isSelectedThisMonth,
  prev12MonthsTotal,
  monthTotal,
  onMonthChange,
  screenSize,
}: SubscriptionsChartProps) => {
  const { l10n } = useLocalization();

  const labelPrev12 = l10n.getString(
    "subscriptions-chart-label-previous-12-months",
    null,
    "Previous 12 months"
  );
  const labelThisMonth = l10n.getString(
    "subscriptions-chart-label-this-month",
    null,
    "(This month)"
  );

  const config = useMemo(
    () => modules.subscriptions.webConfigs[screenSize],
    [screenSize]
  );

  const formatTick = useCallback(
    (d: unknown) => {
      const label = String(d);
      return monthLabels[label] ?? label;
    },
    [monthLabels]
  );

  const handleBarClick = useCallback(
    (_: any, props: any) => {
      onMonthChange(props.datum.month);
    },
    [onMonthChange]
  );

  const upperY = useMemo(() => {
    const maxTotal = Math.max(...data.map((d) => d.total));
    return maxTotal === 0 ? 1 : maxTotal * config.yFactor;
  }, [data, config.yFactor]);

  const tickValues = useMemo(
    () => data.map((datum: DataPoint) => datum.month),
    [data]
  );

  const domain = useMemo<{ x: [number, number]; y: [number, number] }>(
    () => ({
      x: [0, data.length + 1],
      y: [0, upperY],
    }),
    [data.length, upperY]
  );

  const axisHiddenStyle = useMemo(
    () => ({
      axis: { stroke: "transparent" },
      ticks: { stroke: "transparent" },
      tickLabels: { fill: "transparent" },
    }),
    []
  );

  const bottomAxisStyle = useMemo(
    () => ({
      axis: { stroke: "transparent" },
      ticks: { stroke: "transparent" },
      tickLabels: {
        fontSize: config.fontSize.tick,
        padding: config.tickPadding,
      },
    }),
    [config.fontSize.tick, config.tickPadding]
  );

  const domainPadding = useMemo(
    () => ({ x: config.domainPaddingX }),
    [config.domainPaddingX]
  );

  const barStyle = useMemo(
    () => ({
      data: {
        fill: ({ datum }: any): string =>
          datum.month === selectedMonth
            ? "var(--theme-primary)"
            : "var(--bgColor-cloudGrey)",
      },
    }),
    [selectedMonth]
  );

  const events = useMemo(
    () => [
      {
        target: "data" as const,
        eventHandlers: {
          onClick: handleBarClick,
        },
      },
    ],
    [handleBarClick]
  );

  const goPrev = useCallback(() => {
    if (prevMonth) {
      onMonthChange(prevMonth);
    }
  }, [prevMonth, onMonthChange]);

  const goNext = useCallback(() => {
    if (nextMonth) {
      onMonthChange(nextMonth);
    }
  }, [nextMonth, onMonthChange]);

  return (
    <div className={`${styles.container} rounded--all padding--all--m`}>
      {/* Navigation */}
      <div className={styles.navigationContainer}>
        <div className={styles.selectedMonth}>
          {selectedMonthFullName} {year}
        </div>
        <div className={styles.navigation}>
          <button
            name="chevron-left"
            className={styles.directionBtn}
            disabled={!prevMonth}
            onClick={goPrev}
          >
            <span className="narmi-icon-chevron-left" />
          </button>
          <button
            name="chevron-right"
            className={styles.directionBtn}
            disabled={!nextMonth}
            onClick={goNext}
          >
            <span className="narmi-icon-chevron-right" />
          </button>
        </div>
      </div>

      {/* Summary */}
      <ContentCard kind="bordered">
        <div className={styles.summary}>
          <div>
            <div className={styles.summaryValue}>
              {Filters.currency(dollars(prev12MonthsTotal), {
                hasDecimal: true,
              })}
            </div>
            <div className="fontSize--xs fontColor--secondary">
              {labelPrev12}
            </div>
          </div>
          <div className={styles.rightSummary}>
            <div className={styles.summaryValue}>${monthTotal.toFixed(2)}</div>
            <div className="fontSize--xs fontColor--secondary">
              {selectedMonthFullName}
              {isSelectedThisMonth && ` ${labelThisMonth}`}
            </div>
          </div>
        </div>

        <VictoryChart
          height={config.height}
          padding={config.padding}
          domain={domain}
          domainPadding={domainPadding}
        >
          {/* Hide left axis */}
          <VictoryAxis dependentAxis style={axisHiddenStyle} />
          {/* Bottom axis labels only */}
          <VictoryAxis
            tickValues={tickValues}
            tickFormat={formatTick}
            style={bottomAxisStyle}
          />
          <VictoryBar
            data={data}
            x="month"
            y="total"
            barWidth={config.barWidth}
            cornerRadius={config.cornerRadius}
            style={barStyle}
            events={events}
          />
        </VictoryChart>
      </ContentCard>
    </div>
  );
};

export default SubscriptionsChart;
